import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Edit2, Trash2 } from 'lucide-react';
import type { Device, Unit } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { SortToolbar } from '@/components/ui/sort-toolbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatRelativeTime } from '@/lib/utils';
import { compareUnitNumbers, sortUnitsForDisplay } from '@/features/units/utils/sort-units-for-display';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';
import { deviceTypeLabel, formatUnitOfMeasureSuffix } from '@/features/devices/utils/salt-config';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { PropertyUnitCard } from './property-unit-card';

// May 15 batch — new default tab on the Property Portal. Lists every
// unit in the property (or in the selected building when the sidebar
// filter is active), sorted ascending (numeric-prefix-first natural
// sort), with per-unit device count + status + inline edit/delete.
//
// Devices tab covers the "all devices assigned to units" view; this
// tab is the unit-of-record list — the canonical answer to "what units
// does this property have?".

type SortKey = 'unit' | 'building' | 'devices';
type SortDir = 'asc' | 'desc';

const SORT_STORAGE_KEY = 'norgas.propertyPortal.unitsSort';

// Match the Devices tab's options so the user's "I prefer 50/page"
// muscle memory carries across tabs. Default 25 matches Devices too.
const UNITS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
type UnitsPerPage = typeof UNITS_PER_PAGE_OPTIONS[number];
const DEFAULT_PER_PAGE: UnitsPerPage = 25;
const PER_PAGE_STORAGE_KEY = 'norgas.propertyPortal.unitsPerPage';

function readStoredPerPage(): UnitsPerPage {
  if (typeof window === 'undefined') return DEFAULT_PER_PAGE;
  try {
    const raw = window.localStorage.getItem(PER_PAGE_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return (UNITS_PER_PAGE_OPTIONS as readonly number[]).includes(parsed)
      ? (parsed as UnitsPerPage)
      : DEFAULT_PER_PAGE;
  } catch {
    return DEFAULT_PER_PAGE;
  }
}

function writeStoredPerPage(value: UnitsPerPage) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PER_PAGE_STORAGE_KEY, String(value));
  } catch {
    // Safari private mode / quota — fall through to in-memory only.
  }
}

// May 15 batch — filter units by device-presence. Persisted per-user so
// the choice survives navigation; resets to page 1 on change to avoid
// stranding the user on an empty page after the visible list shrinks.
type DeviceFilter = 'all' | 'with' | 'without';
const DEVICE_FILTER_STORAGE_KEY = 'norgas.propertyPortal.unitsDeviceFilter';

function readStoredDeviceFilter(): DeviceFilter {
  if (typeof window === 'undefined') return 'all';
  try {
    const raw = window.localStorage.getItem(DEVICE_FILTER_STORAGE_KEY);
    return raw === 'with' || raw === 'without' ? raw : 'all';
  } catch {
    return 'all';
  }
}

function writeStoredDeviceFilter(value: DeviceFilter) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEVICE_FILTER_STORAGE_KEY, value);
  } catch {
    // Ignore — in-memory state still works.
  }
}

function readStoredSort(): { sortBy: SortKey; sortDir: SortDir } {
  if (typeof window === 'undefined') return { sortBy: 'unit', sortDir: 'asc' };
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return { sortBy: 'unit', sortDir: 'asc' };
    const parsed = JSON.parse(raw) as { sortBy?: string; sortDir?: string };
    const sortBy: SortKey =
      parsed.sortBy === 'building' || parsed.sortBy === 'devices'
        ? parsed.sortBy
        : 'unit';
    const sortDir: SortDir = parsed.sortDir === 'desc' ? 'desc' : 'asc';
    return { sortBy, sortDir };
  } catch {
    return { sortBy: 'unit', sortDir: 'asc' };
  }
}

function writeStoredSort(sortBy: SortKey, sortDir: SortDir) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ sortBy, sortDir }));
  } catch {
    // Safari private mode / quota — sort state degrades to in-memory only.
  }
}

interface Props {
  propertyId: string;
  units: Unit[];
  devices: Device[];
  canWrite: boolean;
  viewMode: 'grid' | 'list';
  onEditUnit: (unit: Unit) => void;
  onDeleteUnit: (unit: Unit) => void;
  onAddUnit: () => void;
}

export function PropertyUnitsTab({
  propertyId,
  units,
  devices,
  canWrite,
  viewMode,
  onEditUnit,
  onDeleteUnit,
  onAddUnit,
}: Props) {
  const navigate = useNavigate();
  const [sortState, setSortState] = useState<{ sortBy: SortKey; sortDir: SortDir }>(() => readStoredSort());
  const [page, setPage] = useState(1);
  const [perPage, setPerPageState] = useState<UnitsPerPage>(() => readStoredPerPage());
  const [deviceFilter, setDeviceFilterState] = useState<DeviceFilter>(() => readStoredDeviceFilter());

  function handleSort(nextSortBy: SortKey) {
    setSortState((prev) => {
      const flipping = prev.sortBy === nextSortBy;
      const next: { sortBy: SortKey; sortDir: SortDir } = {
        sortBy: nextSortBy,
        sortDir: flipping ? (prev.sortDir === 'asc' ? 'desc' : 'asc') : 'asc',
      };
      writeStoredSort(next.sortBy, next.sortDir);
      return next;
    });
    // Sort changes dump you into a different ordering — jump back to
    // page 1 so the user lands at the new top of the list.
    setPage(1);
  }

  function setPerPage(value: UnitsPerPage) {
    setPerPageState(value);
    writeStoredPerPage(value);
    setPage(1);
  }

  function setDeviceFilter(value: DeviceFilter) {
    setDeviceFilterState(value);
    writeStoredDeviceFilter(value);
    setPage(1);
  }

  // Devices grouped by unitId so per-row count is a single hash lookup.
  const devicesByUnit = useMemo(() => {
    const map = new Map<string, Device[]>();
    devices.forEach((d) => {
      if (!d.unitId) return;
      const arr = map.get(d.unitId) ?? [];
      arr.push(d);
      map.set(d.unitId, arr);
    });
    return map;
  }, [devices]);

  // Device-presence filter runs BEFORE sort + pagination so the total
  // count, page math, and "Showing X of Y" line all reflect the user's
  // chosen scope.
  const filteredUnits = useMemo(() => {
    if (deviceFilter === 'all') return units;
    return units.filter((u) => {
      const has = (devicesByUnit.get(u.id)?.length ?? 0) > 0;
      return deviceFilter === 'with' ? has : !has;
    });
  }, [units, devicesByUnit, deviceFilter]);

  const orderedUnits = useMemo(() => {
    const sorted = (() => {
      switch (sortState.sortBy) {
        case 'unit':
          return sortUnitsForDisplay(filteredUnits);
        case 'building':
          // Building name natural-compared, then unit number as the
          // tiebreaker so within a building the natural order holds.
          return [...filteredUnits].sort((a, b) => {
            const bd = (a.buildingName ?? '').localeCompare(b.buildingName ?? '', undefined, {
              numeric: true, sensitivity: 'base',
            });
            return bd !== 0 ? bd : compareUnitNumbers(a.unitNumber, b.unitNumber);
          });
        case 'devices':
          return [...filteredUnits].sort((a, b) => {
            const ad = devicesByUnit.get(a.id)?.length ?? 0;
            const bd = devicesByUnit.get(b.id)?.length ?? 0;
            const diff = ad - bd;
            return diff !== 0 ? diff : compareUnitNumbers(a.unitNumber, b.unitNumber);
          });
      }
    })();
    return sortState.sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [filteredUnits, devicesByUnit, sortState]);

  // Total count drives the pagination summary + page math. Reset back
  // to page 1 whenever the underlying length changes (e.g., the sidebar
  // building filter narrows the visible units) so the user doesn't end
  // up on an empty page that no longer has data.
  const totalCount = orderedUnits.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);
  const pageUnits = useMemo(() => {
    const start = (page - 1) * perPage;
    return orderedUnits.slice(start, start + perPage);
  }, [orderedUnits, page, perPage]);

  // Empty state branches on WHY the list is empty:
  //   - No units in the property at all → "No units yet" + Add CTA
  //   - Filter narrowed to zero       → "No units match" + clear filter
  const hasUnits = units.length > 0;
  const filterActive = deviceFilter !== 'all';

  return (
    <div className="flex flex-col gap-3">
      {/* Filter + Sort toolbar at the top — flex-wraps gracefully on
          narrow viewports so the SortToolbar drops below the filter
          when there's not enough horizontal room. Sortable column
          headers stay where they exist and reflect the same state. */}
      {hasUnits && (
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <div
            role="group"
            aria-label="Filter units by device presence"
            className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5"
          >
            {(['all', 'with', 'without'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setDeviceFilter(value)}
                aria-pressed={deviceFilter === value}
                className={cn(
                  'inline-flex items-center justify-center px-3 h-9 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                  deviceFilter === value
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
                )}
              >
                {value === 'all' ? 'All' : value === 'with' ? 'With devices' : 'Without devices'}
              </button>
            ))}
          </div>
          <SortToolbar
            sortBy={sortState.sortBy}
            sortDir={sortState.sortDir}
            onSortByChange={(by) => {
              setSortState((prev) => {
                const next = { sortBy: by, sortDir: prev.sortDir };
                writeStoredSort(next.sortBy, next.sortDir);
                return next;
              });
              setPage(1);
            }}
            onSortDirChange={(dir) => {
              setSortState((prev) => {
                const next = { sortBy: prev.sortBy, sortDir: dir };
                writeStoredSort(next.sortBy, next.sortDir);
                return next;
              });
              setPage(1);
            }}
            options={[
              { value: 'unit', label: 'Unit number' },
              { value: 'building', label: 'Building' },
              { value: 'devices', label: 'Device count' },
            ]}
          />
        </div>
      )}

      {orderedUnits.length === 0 ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center">
          <EmptyState
            title={hasUnits && filterActive ? 'No units match this filter' : 'No units yet'}
            description={
              hasUnits && filterActive
                ? 'Try a different filter or add a unit.'
                : 'Add a unit to start tracking meters on this property.'
            }
            action={
              hasUnits && filterActive
                ? { label: 'Clear filter', onClick: () => setDeviceFilter('all') }
                : canWrite
                  ? { label: 'Add Unit', onClick: onAddUnit }
                  : undefined
            }
          />
        </Card>
      ) : viewMode === 'grid' ? (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3"
          role="list"
          aria-label="Units"
        >
          {pageUnits.map((unit) => (
            <PropertyUnitCard
              key={unit.id}
              unit={unit}
              devices={devicesByUnit.get(unit.id) ?? []}
              propertyId={propertyId}
              canWrite={canWrite}
              onEdit={onEditUnit}
              onDelete={onDeleteUnit}
            />
          ))}
        </div>
      ) : (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
        <Table density="compact">
          <TableHeader>
            <TableRow>
              <SortableTableHead
                label="Unit"
                active={sortState.sortBy === 'unit'}
                direction={sortState.sortDir}
                onClick={() => handleSort('unit')}
              />
              <SortableTableHead
                label="Building"
                active={sortState.sortBy === 'building'}
                direction={sortState.sortDir}
                onClick={() => handleSort('building')}
              />
              <SortableTableHead
                label="Devices"
                active={sortState.sortBy === 'devices'}
                direction={sortState.sortDir}
                onClick={() => handleSort('devices')}
              />
              {canWrite && <TableHead className="text-right" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageUnits.map((unit) => {
              const unitDevices = devicesByUnit.get(unit.id) ?? [];
              const hasAlarms = unitDevices.some((d) => (d.activeAlerts?.length ?? 0) > 0);
              // Single-device units click straight through to the
              // device's detail page — installers asked for this so
              // they don't have to do the extra hop through the unit
              // page just to see the reading. Units with 0 or 2+
              // devices keep the unit-page click target since the
              // device choice is ambiguous (or absent). The source
              // context carries the property id so the back button
              // returns to the property portal.
              const clickTarget = unitDevices.length === 1
                ? deviceDetailPath(unitDevices[0].id, { kind: 'property', id: propertyId })
                : `/units/${unit.id}`;
              return (
                <TableRow
                  key={unit.id}
                  onClick={() => navigate(clickTarget)}
                  className={cn(
                    'cursor-pointer group',
                    hasAlarms && 'border-l-[3px] border-l-error-500',
                  )}
                >
                  <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                    {unit.unitNumber}
                  </TableCell>
                  <TableCell className="text-gray-700 dark:text-gray-300">
                    {unit.buildingName}
                  </TableCell>
                  <TableCell>
                    <DevicesCell devices={unitDevices} />
                  </TableCell>
                  {canWrite && (
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditUnit(unit)}
                          title="Edit unit"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteUnit(unit)}
                          title="Delete unit"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      {orderedUnits.length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={setPage}
          perPage={perPage}
          perPageOptions={UNITS_PER_PAGE_OPTIONS}
          onPerPageChange={(n) => setPerPage(n as UnitsPerPage)}
          itemNoun={{ singular: 'unit', plural: 'units' }}
        />
      )}
    </div>
  );
}

function DevicesCell({ devices }: { devices: Device[] }) {
  if (devices.length === 0) {
    return <span className="text-xs italic text-gray-400">No devices</span>;
  }
  const totalAlarms = devices.reduce((s, d) => s + (d.activeAlerts?.length ?? 0), 0);
  const primary = devices[0];
  const tx = primary.latestTransmission;
  const isFresh = hasFreshTransmission(primary);
  const reading = isFresh && tx ? (tx.computedWaterTotalization ?? tx.waterTotalization) : null;
  const readingUnit = formatUnitOfMeasureSuffix(tx?.computedUnitOfMeasure ?? tx?.unitOfMeasure);
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          aria-hidden="true"
          className={cn(
            'inline-block w-2 h-2 rounded-full flex-shrink-0',
            (primary.activeAlerts?.length ?? 0) > 0
              ? 'bg-error-500'
              : isFresh
                ? 'bg-success-500'
                : 'bg-gray-400',
          )}
        />
        <span className="font-mono text-xs truncate">{primary.serialNumber}</span>
        {devices.length > 1 && (
          <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">
            +{devices.length - 1}
          </span>
        )}
        {totalAlarms > 0 && (
          <Badge variant="error" size="sm" className="flex-shrink-0">
            <AlertTriangle className="w-3 h-3" />
            {totalAlarms}
          </Badge>
        )}
      </div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
        <span className="truncate">{deviceTypeLabel(primary)}</span>
      </div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
        {reading != null ? (
          <span>{reading.toLocaleString()} {readingUnit}</span>
        ) : primary.lastSeenAt ? (
          <span>Last seen {formatRelativeTime(primary.lastSeenAt)}</span>
        ) : (
          <span className="italic">Awaiting uplink</span>
        )}
      </div>
    </div>
  );
}

